import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { useDebounce } from '@hooks/useDebounce'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { fetchPlanningPreview } from '../services/fetchPlanningPreview'

export { usePlanningPreview, queryKey }

const queryKey = 'planning-preview'

/**
 * Calculates the eindUitvoeringWeek for a proposed planning, without
 * saving it. `isCalculating` is true from the moment the input changes until
 * the result is back, so callers can hold back saving.
 */
const usePlanningPreview = ({
  id,
  startUitvoeringWeek,
  doorlooptijdWeken,
}: {
  id: string
  startUitvoeringWeek?: string
  doorlooptijdWeken?: number
}) => {
  const { hasPermissions } = useAuthenticatedUser()

  const debouncedStart = useDebounce(startUitvoeringWeek)
  const debouncedDoorlooptijd = useDebounce(doorlooptijdWeken)

  const isValidInput = !!startUitvoeringWeek && !!doorlooptijdWeken
  const isDebouncing =
    startUitvoeringWeek !== debouncedStart ||
    doorlooptijdWeken !== debouncedDoorlooptijd

  const { data, isFetching } = useQuery({
    queryKey: [queryKey, id, debouncedStart, debouncedDoorlooptijd],
    queryFn: () =>
      fetchPlanningPreview({
        id,
        startUitvoeringWeek: debouncedStart as string,
        doorlooptijdWeken: debouncedDoorlooptijd as number,
      }),
    enabled:
      hasPermissions && !!id && !!debouncedStart && !!debouncedDoorlooptijd,
    placeholderData: keepPreviousData,
  })

  return {
    data: isValidInput ? data : undefined,
    isCalculating: isValidInput && (isDebouncing || isFetching),
  }
}
